import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { mqMin } from 'utils/responsive';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 40px;
`;

const Title = styled.h1`
    font-weight: 700;
    font-size: 18px;
`;

const QAContainer = styled.section`
    width: 88.37209302325581%;
    border-top: solid 2px var(--dark-blue);
    border-bottom: solid 2px var(--dark-blue);
    margin: 10px;

    ${mqMin('NARROW')} {
        padding: 10px 0 10px 0;
    }
`;

const Item = styled.section`
    width: 100%;
    border-bottom: solid 1px #dadada;

    p {
        font-size: var(--font-medium);
        font-weight: var(--font-bold);
    }
`;

const QButton = styled.div`
    width: 100%;
    position: relative;
    background-color: #fff;
    color: var(--dark-blue);
    padding: 0;
    cursor: pointer;
`;

const QTextContainer = styled.div`
    display: flex;
    align-items: center;

    p {
        width: calc(100% - 24px);
        padding: 12px 20px;
        text-align: left;
        line-height: 1.5em;
        letter-spacing: 0.1em;
    }
`;

const QMark = styled.div<{ isOpen: boolean }>`
    position: relative;
    display: block;
    width: 20px;
    height: 20px;
    margin-right: 15px;
    span:nth-of-type(1) {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        display: block;
        width: 100%;
        height: 2px;
        background-color: var(--dark-blue);
    }
    span:nth-of-type(2) {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        display: block;
        width: 2px;
        height: 100%;
        background-color: var(--dark-blue);
        transition: transform 0.2s;
        transform: ${({ isOpen }) => (isOpen ? 'rotate(90deg)' : 'rotate(0deg)')};
    }
`;

const AItem = styled.div<{ isOpen: boolean }>`
    width: 100%;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;
    background-color: var(--white);
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
`;

const AItemText = styled.div`
    padding: 0 20px 20px 20px;
    text-align: left;
    line-height: 1.7em;
    letter-spacing: 0.1em;
    font-weight: 600;

    p:nth-of-type(2) {
        color: var(--dark-blue);
    }
`;

const OrangeText = styled.p`
    color: var(--orange);
`;

const faqData = [
    {
        question: '仲介会社によって、どれくらい違いがあるのですか？',
        answer: (
            <>
                <OrangeText>仲介会社ごとに得意な業種・規模・買い手ネットワーク・実績が異なります。</OrangeText>
                <p>
                    大手は幅広い買い手を抱えていますが、地域密着型や業界特化型の仲介会社も、独自の買い手を持っていたり、親身な対応が強みだったりします。{' '}
                    <br />
                    ご自身の会社の特徴に合ったアドバイザーを選ぶことが、納得できるM&Aの第一歩です。
                </p>
            </>
        ),
    },
    {
        question: '成功報酬など手数料の相場がよくわかりません。高いと損ですか？',
        answer: (
            <>
                <OrangeText>手数料は成功報酬型が一般的ですが、金額は会社規模や売却価格によって異なります。</OrangeText>
                <p>
                    単純に「安い＝得」ではなく、「実際に良い相手とマッチングしてくれるか」「過去の実績」なども含めて総合的に判断することが大切です。
                    <br />
                    複数の仲介会社に見積もりや説明を受け、比較検討するのがおすすめです。
                </p>
            </>
        ),
    },
    {
        question: '専任契約って、結んでも大丈夫ですか？',
        answer: (
            <>
                <OrangeText>専任契約を結ぶと、他の仲介会社と相談・取引ができなくなります。</OrangeText>
                <p>
                    信頼できる仲介会社と出会えれば良いのですが、「最初の1社だけ」で決めてしまうと、他の選択肢を見逃す可能性もあります。
                    <br />
                    そのため、専任契約を結ぶ前に、複数の仲介会社の意見（セカンドオピニオン）を聞くことを推奨します。
                </p>
            </>
        ),
    },
    {
        question: '仲介会社はどうやって買い手を探しているのですか？',
        answer: (
            <>
                <OrangeText>
                    主に自社が抱える買い手リストや、提携先（金融機関・投資会社など）への情報展開、マッチングサイトの活用などで買い手を探しています。
                </OrangeText>
                <p>
                    仲介会社によって得意な業界やネットワークの強さが異なるため、自社に合った買い手層にリーチできるかを確認することが重要です。
                </p>
            </>
        ),
    },
    {
        question: '売却が本当に成立するか、不安です…',
        answer: (
            <>
                <OrangeText> M&Aは「縁とタイミング」が大きく影響します。</OrangeText>
                <p>
                    売却が成立しないケースもありますが、適切な仲介会社と連携し、準備・調整を行えば、成功確率は大きく高まります。{' '}
                    <br />
                    成約実績や支援体制、フォローの丁寧さなどを確認して、信頼できるパートナーを見つけましょう。
                </p>
            </>
        ),
    },
    {
        question: '秘密はちゃんと守られるのでしょうか？',
        answer: (
            <>
                <OrangeText>
                    基本的に、仲介会社は秘密保持契約（NDA）を買い手と締結した上で情報開示を行います。
                </OrangeText>
                <p>
                    また、段階的に匿名で話を進められるプラットフォームや支援スキームを選ぶことで、リスクを最小限にできます。
                    <br />
                    最初の相談時点で、秘密保持体制をしっかり確認するのがおすすめです。
                </p>
            </>
        ),
    },
    {
        question: '仲介会社の言う「高く売れる」は本当でしょうか？',
        answer: (
            <>
                <OrangeText>
                    査定価格や売却見込み価格は、あくまで“目安”であり、買い手の意向や市場環境によって大きく変動します。
                </OrangeText>
                <p>
                    1社だけの意見を鵜呑みにせず、複数社の査定を受けて、価格だけでなく根拠や戦略も比較することが大切です。
                </p>
            </>
        ),
    },
];

export const QA: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

    const toggleFAQ = (index: number) => {
        setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
    };

    useEffect(() => {
        answerRefs.current.forEach((ref, index) => {
            if (ref) {
                if (openIndex === index) {
                    ref.style.maxHeight = ref.scrollHeight + 'px';
                } else {
                    ref.style.maxHeight = '0';
                }
            }
        });
    }, [openIndex]);

    return (
        <Wrapper>
            <Title>よくあるご質問</Title>
            <QAContainer>
                {faqData.map((faq, index) => (
                    <Item key={index}>
                        <QButton className="topFaq_item_q" onClick={() => toggleFAQ(index)}>
                            <QTextContainer className="topFaq_item_q_text">
                                <p>{faq.question}</p>
                                <QMark isOpen={openIndex === index}>
                                    <span className="topFaq_item_q_mark_line01"></span>
                                    <span className="topFaq_item_q_mark_line02"></span>
                                </QMark>
                            </QTextContainer>
                        </QButton>
                        <AItem ref={(el) => (answerRefs.current[index] = el)} isOpen={openIndex === index}>
                            <AItemText>{faq.answer}</AItemText>
                        </AItem>
                    </Item>
                ))}
            </QAContainer>
        </Wrapper>
    );
};
